import React, { useState, useEffect, useRef } from 'react';
import { ChevronDownIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { authService } from '../../services';

export default function CountrySelector({ value, onChange, error, className = '', label }) {
  const { t } = useTranslation();
  const [countries, setCountries] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const dropdownRef = useRef(null);

  // Default to Egypt (EG) if no value is provided
  const selectedValue = value || 'EG';

  useEffect(() => {
    fetchCountries();

    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchCountries = async () => {
    try {
      const response = await authService.getCountries();
      setCountries(response.data);
    } catch (error) {
      console.error('Failed to fetch countries:', error);
    } finally {
      setLoading(false);
    }
  };

  const selectedCountry = countries.find(c => c.code === value);
  const filteredCountries = countries.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    c.countryCode.includes(search)
  );

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          {label}
        </label>
      )}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`input flex items-center justify-between w-full h-11 px-4 ${error ? 'border-danger' : ''
          } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
        disabled={loading}
        aria-label={t('auth.selectCountry') || 'Select Country'}
      >
        <div className="flex items-center gap-3 min-w-0 flex-1">
          {selectedCountry ? (
            <>
              <span className="text-2xl flex-shrink-0">{selectedCountry.flag}</span>
              <div className="min-w-0 flex-1">
                {/* <div className="text-sm font-medium text-neutral-dark truncate">
                  {selectedCountry.name}
                </div> */}
                <div className="text-xs text-gray-500">
                  {selectedCountry.countryCode}
                </div>
              </div>
            </>
          ) : (
            <span className="text-gray-400 text-sm">{t('auth.selectCountry') || 'Select Country'}</span>
          )}
        </div>
        <ChevronDownIcon className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute z-50 w-full mt-2 bg-white border border-gray-200 rounded-lg shadow-xl max-h-80 overflow-hidden">
          <div className="p-3 border-b border-gray-200 bg-gray-50">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('common.search') || 'Search...'}
                className="input pl-10 pr-4 w-full h-10 text-sm"
                onClick={(e) => e.stopPropagation()}
                autoFocus
              />
            </div>
          </div>
          <div className="overflow-y-auto max-h-64">
            {filteredCountries.length > 0 ? (
              filteredCountries.map((country) => (
                <button
                  key={country.code}
                  type="button"
                  onClick={() => {
                    if (onChange) {
                      onChange(country.code);
                    }
                    setIsOpen(false);
                    setSearch('');
                  }}
                  className={`w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 transition-colors ${selectedValue === country.code ? 'bg-primary/5' : ''
                    }`}
                >
                  {/* <span className="text-2xl flex-shrink-0">{country.flag}</span> */}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm text-neutral-dark">{country.name}</div>
                    <div className="text-xs text-gray-500 mt-0.5">{country.countryCode}</div>
                  </div>
                  {selectedValue === country.code && (
                    <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0"></div>
                  )}
                </button>
              ))
            ) : (
              <div className="px-4 py-8 text-center text-gray-500 text-sm">
                {t('common.noResults') || 'No results found'}
              </div>
            )}
          </div>
        </div>
      )}

      {error && <p className="mt-1.5 text-sm text-danger">{error}</p>}
    </div>
  );
}
